/**
 * Setup Progress Indicator
 *
 * Shows setup completion progress with visual progress bar and next action.
 */

import { Box, LinearProgress, Typography } from '@mui/material';

interface SetupProgressIndicatorProps {
  hasConnection: boolean;
  hasIdentities: boolean;
  allIdentitiesVerified: boolean;
  hasRequiredEnrollments: boolean;
}

interface ProgressState {
  completed: number;
  total: number;
  nextAction: string | null;
}

function calculateProgress({
  hasConnection,
  hasIdentities,
  allIdentitiesVerified,
  hasRequiredEnrollments,
}: SetupProgressIndicatorProps): ProgressState {
  let completed = 0;
  const total = 4;
  let nextAction: string | null = null;

  // Step 1: Clearinghouse connection
  if (hasConnection) {
    completed++;
  } else {
    nextAction = 'Connect to clearinghouse';
    return { completed, total, nextAction };
  }

  // Step 2a: At least one billing identity exists
  if (hasIdentities) {
    completed++;
  } else {
    nextAction = 'Add your billing identity';
    return { completed, total, nextAction };
  }

  // Step 2b: All billing identities verified
  if (allIdentitiesVerified) {
    completed++;
  } else {
    nextAction = 'Finish setting up your billing identity';
    return { completed, total, nextAction };
  }

  // Step 3: Required payer enrollments (optional step)
  if (hasRequiredEnrollments) {
    completed++;
    nextAction = null; // All done!
  } else {
    // This is optional, so we're essentially done
    nextAction = 'Enroll with payers (optional)';
  }

  return { completed, total, nextAction };
}

export function SetupProgressIndicator(props: SetupProgressIndicatorProps) {
  const { completed, total, nextAction } = calculateProgress(props);
  const percentage = (completed / total) * 100;
  const isComplete = completed === total;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {isComplete ? (
            <>✓ Ready to submit claims!</>
          ) : (
            <>
              {completed} of {total} steps complete
            </>
          )}
        </Typography>
        {!isComplete && nextAction && (
          <Typography variant="caption" color="text.secondary">
            Next: {nextAction}
          </Typography>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 1,
            backgroundColor: isComplete ? 'success.main' : 'primary.main',
          },
        }}
      />
    </Box>
  );
}
